import CustomProvider from 'components/CustomProvider';
import { useCallback, useContext, useReducer, useRef, ReactNode, createContext } from 'react';
import { Context } from '@alltrails/shared/types/context';
import useWindowWidth from '../../../hooks/useWindowWidth';

// used for trail card analytics
enum PageSource {
  Home = 'homepage',
  Explore = 'explore',
  Trail = 'trail',
  Seo = 'seo',
  Unknown = 'unknown'
}

type PageState = {
  context: Context;
  pageSource: PageSource;
  windowWidth: number;
};

type PageValue = {
  globalState: PageState;
  dispatchGlobalState: React.Dispatch<PageAction>;
  modalRef: React.RefObject<HTMLDivElement>;
};

const PageContext = createContext<PageValue>({
  globalState: { context: {} as Context, pageSource: PageSource.Unknown, windowWidth: 0 },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatchGlobalState: () => {},
  modalRef: null
});

enum PageActionTypes {
  UPDATE_WINDOW_WIDTH = 'UPDATE_WINDOW_WIDTH'
}

type PageAction = {
  type: PageActionTypes.UPDATE_WINDOW_WIDTH;
  payload: number;
};

const globalReducer = (state: PageState, action: PageAction) => {
  switch (action.type) {
    case PageActionTypes.UPDATE_WINDOW_WIDTH:
      return {
        ...state,
        windowWidth: action.payload
      };
    default:
      return state;
  }
};

type PageProviderProps = {
  pageSource?: PageSource;
  context: Context;
  children: ReactNode;
};

const Page = ({ context, pageSource, children }: PageProviderProps): JSX.Element => {
  const [globalState, dispatchGlobalState] = useReducer(globalReducer, { context, pageSource, windowWidth: null });
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatchWindowWidth = useCallback(
    payload => {
      dispatchGlobalState({ type: PageActionTypes.UPDATE_WINDOW_WIDTH, payload });
    },
    [dispatchGlobalState]
  );
  useWindowWidth({ dispatchWindowWidth });

  return (
    <PageContext.Provider value={{ globalState, dispatchGlobalState, modalRef }}>
      <CustomProvider>
        <div id="modalPortal" ref={modalRef} />
        {children}
      </CustomProvider>
    </PageContext.Provider>
  );
};

function usePageContext() {
  const context = useContext(PageContext);

  if (context === undefined) {
    throw new Error('The page context is undefined. Are you using usePageContext within PageContext?');
  }

  return context.globalState.context;
}

export { Page, PageContext, PageActionTypes, PageSource, usePageContext };
